import type {
  ICellRendererParams,
  IRowNode,
} from '@mint-lib/ag-grid/community';
import { LoadingCell } from '@mint-lib/ag-grid/components';
import { useProfile } from '@mint-lib/profile-helpers';

import type { ColumnOption } from '../../../api/queries/userColumnsConfig.js';
import { USER_STATUS } from '../../../constants/userTableV2.js';
import type { UserTableV2Row } from '../datasource.js';
import { isUserUpdating, type UserTableV2GridContext } from '../gridContext.js';
import RoleSelect from './RoleSelect.jsx';

type Props = ICellRendererParams<
  UserTableV2Row,
  string | number | undefined,
  UserTableV2GridContext
> & {
  options?: ColumnOption[];
  onRoleChange?: (
    user: UserTableV2Row,
    newRole: string | number,
    node: IRowNode<UserTableV2Row>,
  ) => void;
};

const RoleCell = ({
  context,
  data,
  value,
  node,
  options = [],
  onRoleChange,
}: Props) => {
  const { profile } = useProfile();
  const isUpdating = isUserUpdating(context, data?.id);

  if (!data || isUpdating) {
    return (
      <div className="flex h-full w-full items-center px-4">
        <LoadingCell />
      </div>
    );
  }

  const isSelf = profile?.id === data.id;
  const isDisabled =
    isSelf ||
    context?.isReadonlyPermission === true ||
    data.status === USER_STATUS.DeactivatedSystem;

  const handleChange = (newRole: string | number) => {
    if (!onRoleChange || !node) return;
    onRoleChange(data, newRole, node);
  };

  return (
    <div className="flex h-full w-full items-center">
      <RoleSelect
        options={options}
        value={value ?? undefined}
        disabled={isDisabled}
        onChange={handleChange}
      />
    </div>
  );
};

export default RoleCell;
